// Consulta de produto pro atendimento: preço, estoque, campanha e foto — e o
// botão que manda tudo isso pro cliente numa mensagem só.
//
// Formato de LISTA, não de cartões grandes. Buscar "piso viva" traz 19
// resultados: quem busca por nome quer COMPARAR, e cartão grande mostra um
// produto e meio por tela. Cada linha traz o que se usa sempre (nome, preço,
// "tem aqui?") e esconde atrás de um clique o que é exceção (o estoque das
// outras lojas).
//
// O que o vendedor vê aqui NÃO é o que o cliente recebe: aqui tem estoque e
// situação (informação interna); a mensagem leva só nome, preço e unidade.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';
import { alertarErro } from '../helpers/alerts';
import backdropClose from '../helpers/backdropClose';

const VERDE = '#3D8B37';
const AZUL_CD = '#6f9fd8';
const LARANJA = '#c47f17'; // filtro de estoque — assunto diferente do de disponibilidade
const FILIAL_CD = '04'; // Central: saldo lá é "dá pra trazer", não "outra loja tem"
// Abaixo disso (celular) a coluna de ações à direita esmaga o nome: as ações
// vão pra baixo. Conta: checkbox + foto + coluna de 118px + folgas ≈ 250px,
// sobrando ~220px pro nome e os dois preços numa tela de 520.
const LARGURA_COLUNA_ACOES = 520;

const dinheiro = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
// Estoque de m² vem quebrado (223.25). Vírgula e sem zero à toa: 223,25 e 10.
const quantidade = (v) => Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
// Os números do estoque saem como o ERP tem, negativo incluído. É o que a tela
// de consulta de produto do `centrao` faz, e o pedido de quem vende
// (18/09/2026): quem lê estoque entende o negativo, e zerar some com ele.
const num = (v) => Number(v) || 0;

function corDaSituacao(nome) {
  const t = String(nome || '').toLowerCase();
  if (t.includes('de linha')) return { fundo: 'rgba(61,139,55,0.14)', texto: '#2b6a27' };
  if (t.includes('encomenda')) return { fundo: 'rgba(245,166,35,0.16)', texto: '#a36a00' };
  return { fundo: 'rgba(180,55,55,0.14)', texto: '#b43737' };
}

// Estoque da rede inteira, num modal só pra isso — por isso cabe o nome da
// loja escrito, e não só o código. Todas as filiais aparecem SEMPRE, zeradas
// inclusive: filial faltando deixa a dúvida "não tem, ou não olhou?".
//
// As três colunas (bruto / reservado / disponível) são as mesmas da consulta de
// produto do `centrao`, que já roda em produção — de lá vieram também "a
// faturar" e "trânsito". A diferença é a coluna de disponível, que o `centrao`
// não tem e foi pedida aqui.
//
// "A faturar" e "Trânsito" só aparecem quando existem em alguma loja: numa tela
// de 360px, coluna zerada só rouba espaço das que importam.
function ModalEstoque({ produto, filialDoUsuario, onFechar }) {
  const linhas = produto.estoque_todas || [];
  const grupo = produto.estoque_grupo || null;
  const temAFaturar = linhas.some((l) => num(l.a_faturar) !== 0);
  const temTransito = linhas.some((l) => num(l.transito) !== 0);

  const cabecalho = {
    padding: '4px 6px', textAlign: 'right', fontWeight: 600, fontSize: '0.7rem',
    textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--portal-muted)',
    borderBottom: '1px solid var(--portal-border)', whiteSpace: 'nowrap',
  };
  const celula = {
    padding: '5px 6px', borderBottom: '1px solid var(--portal-border)',
    textAlign: 'right', whiteSpace: 'nowrap',
  };

  return (
    <div
      {...backdropClose(onFechar)}
      style={{
        position: 'fixed', inset: 0, zIndex: 20050, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--portal-surface)', color: 'var(--portal-text)',
          border: '1px solid var(--portal-border)', borderRadius: 10,
          padding: 16, width: '100%', maxWidth: 460, maxHeight: '86vh', overflowY: 'auto',
        }}
      >
        <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Estoque por loja</div>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.25, marginBottom: 10 }}>
          {produto.descricao}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
          <thead>
            <tr>
              <th style={{ ...cabecalho, textAlign: 'left' }}>Loja</th>
              <th style={cabecalho}>Bruto</th>
              <th style={cabecalho}>Reserv.</th>
              {temAFaturar && <th style={cabecalho}>A fat.</th>}
              {temTransito && <th style={cabecalho}>Trâns.</th>}
              <th style={cabecalho}>Dispon.</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              const minha = String(l.filial) === String(filialDoUsuario);
              const ehCD = String(l.filial) === FILIAL_CD;
              const qtd = num(l.disponivel);
              const reserv = num(l.reservado);
              const destaque = minha ? VERDE : (ehCD ? AZUL_CD : null);
              return (
                <tr key={l.filial} style={{
                  background: minha ? 'rgba(61,139,55,0.12)' : (ehCD ? 'rgba(90,140,200,0.12)' : 'transparent'),
                }}>
                  <td style={{ padding: '5px 6px', borderBottom: '1px solid var(--portal-border)' }}>
                    <span style={{ color: 'var(--portal-muted)', marginRight: 5 }}>{l.filial}</span>
                    <span style={{ color: destaque || 'var(--portal-text)', fontWeight: destaque ? 700 : 400 }}>
                      {l.loja}
                    </span>
                    {minha && <span style={{ color: VERDE, fontSize: '0.72rem', marginLeft: 5 }}>aqui</span>}
                  </td>
                  <td style={{ ...celula, color: 'var(--portal-muted)' }}>{quantidade(num(l.bruto))}</td>
                  {/* Reservado só ganha cor quando existe: laranja em toda
                      linha zerada viraria alarme sem alarme nenhum. */}
                  <td style={{ ...celula, color: reserv > 0 ? LARANJA : 'var(--portal-muted)' }}>
                    {quantidade(reserv)}
                  </td>
                  {temAFaturar && <td style={{ ...celula, color: 'var(--portal-muted)' }}>{quantidade(num(l.a_faturar))}</td>}
                  {temTransito && <td style={{ ...celula, color: 'var(--portal-muted)' }}>{quantidade(num(l.transito))}</td>}
                  <td style={{
                    ...celula, fontWeight: 700,
                    color: qtd > 0 ? 'var(--portal-text)' : '#b43737',
                  }}>
                    {quantidade(qtd)}
                  </td>
                </tr>
              );
            })}
            {grupo && (
              <tr style={{ background: 'var(--portal-bg)' }}>
                <td style={{ padding: '7px 6px', fontWeight: 700 }}>Grupo</td>
                <td style={{ ...celula, fontWeight: 700 }}>{quantidade(num(grupo.bruto))}</td>
                <td style={{ ...celula, fontWeight: 700, color: num(grupo.reservado) > 0 ? LARANJA : 'var(--portal-muted)' }}>
                  {quantidade(num(grupo.reservado))}
                </td>
                {temAFaturar && <td style={{ ...celula, fontWeight: 700 }}>{quantidade(num(grupo.a_faturar))}</td>}
                {temTransito && <td style={{ ...celula, fontWeight: 700 }}>{quantidade(num(grupo.transito))}</td>}
                <td style={{ ...celula, fontWeight: 700, color: num(grupo.disponivel) > 0 ? VERDE : '#b43737' }}>
                  {quantidade(num(grupo.disponivel))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* Só a conta, que é o que a consulta faz de fato
            (`quant - qtdereserv`, ver consultaProduto/repository.js). O que
            cada coluna do ERP significa na operação não está escrito aqui
            porque eu não tenho isso confirmado — e legenda errada na tela do
            vendedor é pior que legenda nenhuma. */}
        <div style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', marginTop: 8, lineHeight: 1.4 }}>
          Disponível = bruto − reservado.
        </div>

        <button
          type="button"
          onClick={onFechar}
          style={{
            background: 'transparent', color: 'var(--portal-muted)', border: 'none',
            padding: '10px 0 0', fontSize: '0.82rem', cursor: 'pointer', width: '100%',
          }}
        >
          fechar
        </button>
      </div>
    </div>
  );
}

// Uma linha da busca, em COLUNAS FIXAS: [☐] [foto] [nome, código, preços] [ações].
//
// Antes as ações ("estoque" e "mandar") desciam pra linha de baixo quando não
// cabiam — e se cabiam ou não dependia do tamanho do nome e de ter foto. Numa
// mesma busca, umas linhas saíam com as ações à direita e outras embaixo, e o
// nome começava em lugares diferentes. Agora a coluna das ações tem largura
// fixa e nunca desce; quem se ajusta é o texto do meio (nome e preço quebram
// dentro da própria coluna). Toda linha tem o mesmo desenho.
//
// Em tela estreita (celular) uma coluna de ações à direita esmagaria o nome, então
// ali as ações ficam SEMPRE embaixo — outro desenho, mas também igual pra todas.
function LinhaProduto({ produto, conversaId, onAmpliar, onMandar, onVerEstoque, naLista, onAlternarLista, largo }) {
  const [temFoto, setTemFoto] = useState(true);
  const cor = corDaSituacao(produto.disponibilidade);
  const aqui = produto.estoque_aqui;
  const grupo = produto.estoque_grupo;

  // Dois números, não um. O daqui responde "entrego agora?"; o do grupo
  // responde "existe na empresa?" — que é a pergunta seguinte do cliente. Em
  // duas linhas curtas em vez de uma comprida: é o que deixa caber numa coluna
  // estreita.
  const botaoEstoque = (
    <button
      type="button"
      onClick={() => onVerEstoque(produto)}
      title="Ver o estoque de cada loja"
      style={{
        background: 'transparent', color: 'var(--portal-text)',
        border: '1px solid var(--portal-border)', borderRadius: 6,
        padding: '3px 8px', fontSize: '0.74rem', lineHeight: 1.35,
        cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ display: 'block' }}>
        <span style={{ color: 'var(--portal-muted)' }}>estoque </span>
        <strong style={{ color: num(aqui?.disponivel) > 0 ? VERDE : '#b43737' }}>
          {quantidade(num(aqui?.disponivel))}
        </strong>
      </span>
      {grupo && (
        <span style={{ display: 'block' }}>
          <span style={{ color: 'var(--portal-muted)' }}>grupo </span>
          <strong style={{ color: num(grupo.disponivel) > 0 ? VERDE : '#b43737' }}>
            {quantidade(num(grupo.disponivel))}
          </strong>
        </span>
      )}
    </button>
  );

  const botaoMandar = conversaId ? (
    <button
      type="button"
      onClick={() => onMandar(produto)}
      style={{
        background: VERDE, color: '#fff', border: `1px solid ${VERDE}`,
        borderRadius: 6, padding: '6px 10px', fontSize: '0.8rem',
        cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
      }}
    >
      <i className="bi bi-send me-1" />mandar
    </button>
  ) : (
    <span style={{ fontSize: '0.72rem', color: 'var(--portal-muted)', lineHeight: 1.3 }}>
      abra uma conversa pra mandar
    </span>
  );

  const acoes = largo ? (
    <div style={{ width: 118, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
      {botaoMandar}
      {botaoEstoque}
    </div>
  ) : (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
      {botaoEstoque}
      {botaoMandar}
    </div>
  );

  return (
    <div style={{
      borderBottom: '1px solid var(--portal-border)', padding: '10px 0',
      display: 'flex', gap: 10, alignItems: 'center',
    }}>
      {/* Seleção pra lista (mandar vários de uma vez). Checkbox na ponta
          esquerda porque é onde toda lista de seleção põe — e-mail, arquivos,
          galeria —, então dispensa rótulo. Quadrado de 22px, mas a área de
          toque é de 40px: no tablet do balcão, alvo menor que isso obriga a
          mirar. Só aparece com conversa aberta, igual ao "mandar". */}
      {conversaId && (
        <button
          type="button"
          role="checkbox"
          aria-checked={Boolean(naLista)}
          aria-label={naLista ? 'Tirar da lista' : 'Pôr na lista'}
          title={naLista ? 'Tirar da lista' : 'Pôr na lista pra mandar junto com outros'}
          onClick={() => onAlternarLista(produto)}
          style={{
            width: 40, height: 40, flexShrink: 0, alignSelf: 'center', margin: '0 -6px 0 -6px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          }}
        >
          <span style={{
            width: 22, height: 22, borderRadius: 5,
            border: `2px solid ${naLista ? VERDE : 'var(--portal-muted)'}`,
            background: naLista ? VERDE : 'transparent',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 15, lineHeight: 1,
          }}>
            {naLista && <i className="bi bi-check-lg" />}
          </span>
        </button>
      )}

      {/* Sem foto, fica a moldura do mesmo tamanho com a caixa do Fio (o
          mesmo ícone do botão "Produto"): se a linha encolhesse, o nome
          começaria mais à esquerda que o das outras. */}
      <span style={{
        width: 52, height: 52, borderRadius: 5, flexShrink: 0, overflow: 'hidden',
        background: 'var(--portal-bg)', border: temFoto ? 'none' : '1px solid var(--portal-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!temFoto && (
          <i className="bi bi-box-seam" aria-hidden="true" style={{ fontSize: 24, color: 'var(--portal-muted)' }} />
        )}
        {temFoto && (
          <img
            src={produto.foto}
            alt={produto.descricao}
            onError={() => setTemFoto(false)}
            onClick={() => onAmpliar(produto)}
            title="Clique pra ampliar"
            style={{
              width: '100%', height: '100%', objectFit: 'cover', display: 'block',
              background: '#f0f0f0', cursor: 'zoom-in',
            }}
          />
        )}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.87rem', lineHeight: 1.25 }}>
          {produto.descricao}
        </div>
        <div style={{ fontSize: '0.73rem', color: 'var(--portal-muted)', margin: '3px 0 6px' }}>
          {produto.codpro}{produto.unidade ? ` · ${produto.unidade}` : ''}
          <span style={{
            marginLeft: 6, background: cor.fundo, color: cor.texto,
            padding: '0 6px', borderRadius: 9, fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            {produto.disponibilidade}
          </span>
        </div>
        {/* Preço num bloco próprio, com fundo suave: sem a separação, o olho
            tem que ler nome, código e situação pra achar o número. Os dois
            preços quebram um embaixo do outro quando a coluna aperta. */}
        <div style={{
          fontSize: '0.84rem', display: 'inline-flex', columnGap: 10, rowGap: 2, flexWrap: 'wrap',
          background: 'var(--portal-bg)', border: '1px solid var(--portal-border)',
          borderRadius: 6, padding: '4px 10px',
        }}>
          <span style={{ whiteSpace: 'nowrap' }}>
            {produto.campanha ? (
              <>
                <span style={{
                  textDecoration: 'line-through', color: 'var(--portal-muted)',
                  marginRight: 5, fontSize: '0.76rem',
                }}>
                  {dinheiro(produto.preco_vista)}
                </span>
                <strong style={{ color: '#b43737' }}>{dinheiro(produto.campanha.por)}</strong>
              </>
            ) : <strong>{dinheiro(produto.preco_vista)}</strong>}
            <span style={{ color: 'var(--portal-muted)' }}> à vista</span>
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>
            <strong>{dinheiro(produto.preco_prazo)}</strong>
            <span style={{ color: 'var(--portal-muted)' }}> notinha</span>
          </span>
        </div>
        {!largo && acoes}
      </div>

      {largo && acoes}
    </div>
  );
}

// Escolha do que mandar. Aparece só no clique de "mandar": a decisão é uma por
// produto, não precisa ocupar a lista inteira. Os preços vão nos botões pra ele
// escolher vendo o valor, sem voltar pra conferir.
function EscolherEnvio({ produto, conversaId, onFechar }) {
  const [enviando, setEnviando] = useState(false);
  const [temFoto, setTemFoto] = useState(true);

  const enviar = async (modo) => {
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/produtos/${produto.codpro}/enviar`, {
        conversa_id: conversaId, modo,
      });
      onFechar();
      Swal.fire({
        icon: 'success',
        title: 'Mandado pro cliente',
        text: r.data?.data?.com_foto ? 'Foto e preço enviados.' : 'Enviado sem foto (o produto não tem foto cadastrada).',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (e) {
      alertarErro(e, 'Mandar o produto');
      setEnviando(false);
    }
  };

  const opcao = (modo, titulo, valor) => (
    <button
      type="button"
      disabled={enviando}
      onClick={() => enviar(modo)}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        width: '100%', background: 'transparent', color: 'var(--portal-text)',
        border: `1px solid ${VERDE}`, borderRadius: 8, padding: '10px 14px',
        fontSize: '0.88rem', cursor: enviando ? 'wait' : 'pointer', opacity: enviando ? 0.6 : 1,
      }}
    >
      <span style={{ fontWeight: 600 }}>{titulo}</span>
      {valor && <strong style={{ color: VERDE }}>{valor}</strong>}
    </button>
  );

  const precoVista = produto.campanha ? produto.campanha.por : produto.preco_vista;

  return (
    <div
      {...backdropClose(onFechar)}
      style={{
        position: 'fixed', inset: 0, zIndex: 20050, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--portal-surface)', color: 'var(--portal-text)',
          border: '1px solid var(--portal-border)', borderRadius: 10,
          padding: 16, width: '100%', maxWidth: 340,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>Mandar pro cliente</div>
        <div style={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.25, marginBottom: 4 }}>
          {produto.descricao}
        </div>
        {opcao('vista', 'com preço à vista', dinheiro(precoVista))}
        {opcao('prazo', 'com preço da notinha', dinheiro(produto.preco_prazo))}
        {temFoto && opcao('so_foto', 'só a foto', null)}
        {/* Descobre se há foto sem ocupar espaço: se não carregar, a opção some. */}
        <img
          src={produto.foto}
          alt=""
          onError={() => setTemFoto(false)}
          style={{ display: 'none' }}
        />
        <button
          type="button"
          onClick={onFechar}
          disabled={enviando}
          style={{
            background: 'transparent', color: 'var(--portal-muted)', border: 'none',
            padding: '6px 0', fontSize: '0.82rem', cursor: 'pointer', marginTop: 2,
          }}
        >
          cancelar
        </button>
      </div>
    </div>
  );
}

// Miniatura de um item da lista. Produto sem foto fica com a caixa do Fio no
// mesmo quadrado: sem ele os nomes ficariam desalinhados, uns mais à esquerda
// que outros.
function MiniaturaLista({ produto }) {
  const [temFoto, setTemFoto] = useState(true);
  return (
    <span style={{
      width: 44, height: 44, borderRadius: 6, flexShrink: 0, overflow: 'hidden',
      background: 'var(--portal-bg)', border: '1px solid var(--portal-border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {!temFoto && (
        <i className="bi bi-box-seam" aria-hidden="true" style={{ fontSize: 20, color: 'var(--portal-muted)' }} />
      )}
      {temFoto && (
        <img
          src={produto.foto}
          alt=""
          onError={() => setTemFoto(false)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}
    </span>
  );
}

// Mesmas três opções do envio de um produto só, escolhidas UMA vez pra lista
// toda. Os valores não vão nos botões (são vários produtos); cada mensagem sai
// com o preço do próprio produto, pelo mesmo caminho do "mandar".
function EscolherEnvioLista({ quantidade, onEscolher, onFechar }) {
  const opcao = (modo, titulo) => (
    <button
      type="button"
      onClick={() => onEscolher(modo)}
      style={{
        width: '100%', background: 'transparent', color: 'var(--portal-text)',
        border: `1px solid ${VERDE}`, borderRadius: 8, padding: '10px 14px',
        fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer', textAlign: 'left',
      }}
    >
      {titulo}
    </button>
  );
  return (
    <div
      {...backdropClose(onFechar)}
      style={{
        position: 'fixed', inset: 0, zIndex: 20050, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--portal-surface)', color: 'var(--portal-text)',
          border: '1px solid var(--portal-border)', borderRadius: 10,
          padding: 16, width: '100%', maxWidth: 340,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>
          {quantidade === 1 ? 'Mandar 1 produto pro cliente' : `Mandar ${quantidade} produtos pro cliente`}
        </div>
        <div style={{ fontSize: '0.78rem', color: 'var(--portal-muted)', marginBottom: 4 }}>
          {quantidade === 1 ? 'Vai numa mensagem.' : 'Cada produto vai numa mensagem, na ordem da lista.'}
        </div>
        {opcao('vista', 'com preço à vista')}
        {opcao('prazo', 'com preço da notinha')}
        {opcao('so_foto', 'só a foto')}
        <button
          type="button"
          onClick={onFechar}
          style={{
            background: 'transparent', color: 'var(--portal-muted)', border: 'none',
            padding: '6px 0', fontSize: '0.82rem', cursor: 'pointer', marginTop: 2,
          }}
        >
          cancelar
        </button>
      </div>
    </div>
  );
}

export default function ConsultaProdutoModal({ aberto, conversaId, onClose }) {
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState(null); // null = ainda não buscou
  const [buscando, setBuscando] = useState(false);
  const [ampliada, setAmpliada] = useState(null);
  const [mandando, setMandando] = useState(null); // produto escolhido pra enviar
  const [verEstoque, setVerEstoque] = useState(null); // produto com o estoque aberto
  const [soComEstoque, setSoComEstoque] = useState(false);
  const [situacao, setSituacao] = useState(''); // '' = todas
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  // Lista pra mandar vários de uma vez. Guarda o PRODUTO, não só o código:
  // precisa continuar mostrando o que foi escolhido depois de outra busca, que
  // é o ponto dela ("busca piso, põe; busca argamassa, põe; manda tudo").
  const [naLista, setNaLista] = useState([]);
  const [listaAberta, setListaAberta] = useState(false);
  const [escolhendoModoLista, setEscolhendoModoLista] = useState(false);
  const [enviandoLista, setEnviandoLista] = useState(null); // { feitos, total }

  // O modal fica montado fechado, então a lista sobrevive a fechar e reabrir
  // — de propósito, fechar sem querer não pode custar a lista. Mas ela é DO
  // CLIENTE: trocou de conversa, começa vazia, senão a lista montada pra um
  // cliente iria pro próximo.
  useEffect(() => {
    setNaLista([]);
    setListaAberta(false);
  }, [conversaId]);

  // Largo o bastante pra coluna de ações à direita (ver LinhaProduto). Um
  // ouvinte só aqui, e não um por linha: são dezenas de linhas por busca.
  const [largo, setLargo] = useState(() => window.innerWidth >= LARGURA_COLUNA_ACOES);
  useEffect(() => {
    const medir = () => setLargo(window.innerWidth >= LARGURA_COLUNA_ACOES);
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);

  // Filtros no CLIENTE: a busca já trouxe tudo, então estreitar é instantâneo e
  // não custa outra ida ao ERP.
  const situacoes = useMemo(() => {
    const mapa = new Map();
    (resultados || []).forEach((p) => {
      mapa.set(p.disponibilidade, (mapa.get(p.disponibilidade) || 0) + 1);
    });
    return [...mapa.entries()].sort((a, b) => b[1] - a[1]);
  }, [resultados]);

  const temFiltro = soComEstoque || Boolean(situacao);

  const lista = useMemo(() => (resultados || []).filter((p) => {
    if (situacao && p.disponibilidade !== situacao) return false;
    if (soComEstoque && !p.tem_em_algum_lugar) return false;
    return true;
  }), [resultados, situacao, soComEstoque]);

  if (!aberto) return null;

  const estaNaLista = (p) => naLista.some((x) => x.codpro === p.codpro);
  const alternarLista = (p) => {
    if (enviandoLista) return;
    setNaLista((atual) => (atual.some((x) => x.codpro === p.codpro)
      ? atual.filter((x) => x.codpro !== p.codpro)
      : [...atual, p]));
  };

  // Um por vez, na ordem da lista, pelo MESMO endpoint do "mandar" — não existe
  // caminho paralelo de envio. Cada um que sai já sai da lista: se cair no
  // meio, o que sobrou continua lá e é só apertar de novo pra mandar o resto,
  // sem repetir o que o cliente já recebeu.
  const enviarLista = async (modo) => {
    setEscolhendoModoLista(false);
    const fila = [...naLista];
    const total = fila.length;
    let feitos = 0;
    setEnviandoLista({ feitos: 0, total });
    try {
      for (const p of fila) {
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API}/produtos/${p.codpro}/enviar`, { conversa_id: conversaId, modo });
        feitos += 1;
        setNaLista((atual) => atual.filter((x) => x.codpro !== p.codpro));
        setEnviandoLista({ feitos, total });
      }
      setListaAberta(false);
      Swal.fire({
        icon: 'success',
        title: total === 1 ? 'Produto mandado pro cliente' : `${total} produtos mandados pro cliente`,
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      alertarErro(err, feitos
        ? `Parou no ${feitos + 1}º produto — ${feitos} já ${feitos === 1 ? 'foi' : 'foram'}, o resto continua na lista`
        : 'Mandar a lista');
    } finally {
      setEnviandoLista(null);
    }
  };

  // Enquanto a lista está indo, fechar o modal não fecha: o envio continuaria
  // por trás, sem ninguém vendo o que foi e o que falhou.
  const fechar = () => { if (!enviandoLista) onClose(); };

  const buscar = async (e) => {
    e?.preventDefault();
    const t = termo.trim();
    if (t.length < 2) return;
    setBuscando(true);
    // Os filtros PERSISTEM entre buscas: quem ligou "só com estoque" quer isso
    // na próxima busca também. Se nada sobrar, a lista avisa e oferece limpar.
    try {
      const r = await axios.get(`${API}/produtos/buscar`, { params: { q: t } });
      setResultados(r.data?.data || []);
    } catch (err) {
      alertarErro(err, 'Buscar produto');
    } finally { setBuscando(false); }
  };

  // `cor` distingue os dois assuntos do filtro: estoque (laranja) e
  // disponibilidade (verde). Ligado em cores iguais, tudo virava "um monte de
  // chip" e não dava pra ver de relance o que estava filtrando o quê.
  const chip = (ativo, aoClicar, texto, cor = VERDE) => (
    <button
      type="button"
      onClick={aoClicar}
      style={{
        background: ativo ? cor : 'transparent',
        color: ativo ? '#fff' : 'var(--portal-text)',
        border: `1px solid ${ativo ? cor : 'var(--portal-border)'}`,
        borderRadius: 12, padding: '2px 10px', fontSize: '0.75rem', cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {texto}
    </button>
  );

  // Foto ampliada por cima de tudo: o vendedor precisa ver o produto de perto
  // antes de mandar. Fecha clicando em qualquer lugar.
  if (ampliada) {
    return (
      <div
        onClick={() => setAmpliada(null)}
        style={{
          position: 'fixed', inset: 0, zIndex: 20000, background: 'rgba(0,0,0,0.92)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 12, cursor: 'zoom-out', padding: 20,
        }}
      >
        <img
          src={ampliada.foto}
          alt={ampliada.descricao}
          style={{ maxWidth: '92vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 6 }}
        />
        <div style={{ color: '#fff', fontSize: '0.9rem', textAlign: 'center' }}>
          {ampliada.descricao}
          <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.78rem', marginTop: 4 }}>
            cód {ampliada.codpro} · clique pra fechar
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.72)' }} {...backdropClose(fechar)}>
      <div
        className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        style={{ maxWidth: 900 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content" style={{ background: 'var(--portal-surface)', color: 'var(--portal-text)' }}>
          <div className="modal-header" style={{ borderColor: 'var(--portal-border)', paddingBottom: 10 }}>
            <div style={{ minWidth: 0 }}>
              <h5 className="modal-title" style={{ fontSize: '1rem', marginBottom: 2 }}>
                <i className="bi bi-box-seam me-2" />Consultar produto
              </h5>
              <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                Microuniverso · somente consulta, nada é alterado no ERP
              </div>
            </div>
            {/* O btn-close do Bootstrap some no fundo escuro. Botão próprio. */}
            <button
              type="button"
              onClick={fechar}
              disabled={Boolean(enviandoLista)}
              aria-label="Fechar"
              title="Fechar"
              style={{
                background: 'transparent', border: '1px solid var(--portal-border)',
                color: 'var(--portal-text)', borderRadius: 6, width: 32, height: 32,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0, fontSize: '1rem', lineHeight: 1,
              }}
            >
              <i className="bi bi-x-lg" />
            </button>
          </div>

          {/* Lista aberta = a busca atrás fica apagada e não responde ao toque:
              sem isso dava pra confundir o que está NA LISTA com o que está na
              busca. Tocar na parte apagada fecha a lista, como tocar fora de
              qualquer painel. */}
          <div
            className="modal-body"
            style={{
              paddingTop: 12,
              opacity: listaAberta ? 0.25 : 1,
              transition: 'opacity .15s',
              cursor: listaAberta ? 'pointer' : undefined,
            }}
            onClickCapture={listaAberta ? (e) => {
              e.stopPropagation();
              e.preventDefault();
              setListaAberta(false);
            } : undefined}
          >
            <form onSubmit={buscar} style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <input
                className="form-control form-control-sm"
                placeholder="Nome, código ou referência (ex: piso viva, 24434, 58004)"
                value={termo}
                onChange={(ev) => setTermo(ev.target.value)}
                autoFocus
              />
              <button
                type="submit"
                className="btn btn-sm"
                disabled={buscando || termo.trim().length < 2}
                style={{ background: VERDE, color: '#fff', border: 'none', whiteSpace: 'nowrap' }}
              >
                {buscando ? '...' : 'Buscar'}
              </button>
              {/* Filtro é escolha, não decoração: fica atrás de um botão e não
                  ocupa a tela de quem só quer buscar e mandar. O ponto verde
                  avisa que tem filtro ligado mesmo com o painel fechado. */}
              <button
                type="button"
                onClick={() => setFiltrosAbertos((v) => !v)}
                title="Filtros"
                style={{
                  background: filtrosAbertos ? 'var(--portal-bg)' : 'transparent',
                  color: 'var(--portal-text)', border: '1px solid var(--portal-border)',
                  borderRadius: 6, padding: '0 10px', cursor: 'pointer',
                  whiteSpace: 'nowrap', position: 'relative',
                }}
              >
                <i className="bi bi-sliders" />
                {temFiltro && (
                  <span style={{
                    position: 'absolute', top: 4, right: 4, width: 7, height: 7,
                    borderRadius: '50%', background: VERDE,
                  }} />
                )}
              </button>
            </form>

            {/* Filtros só aparecem quando há o que estreitar. A busca traz tudo
                de propósito; filtrar é escolha, não obrigação. */}
            {filtrosAbertos && (
              <div style={{
                display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10,
                padding: '8px 10px', background: 'var(--portal-bg)', borderRadius: 8,
              }}>
                {chip(soComEstoque, () => setSoComEstoque((v) => !v), 'só com estoque', LARANJA)}
                {/* Divisória: estoque de um lado, disponibilidade do outro. São
                    perguntas diferentes e devem parecer diferentes. */}
                <span style={{
                  width: 1, alignSelf: 'stretch', minHeight: 20,
                  background: 'var(--portal-border)', margin: '0 3px',
                }} />
                {/* "disponibilidade" é a palavra do ERP e a que eles usam —
                    "situação" era invenção minha. */}
                {chip(!situacao, () => setSituacao(''), 'todas as disponibilidades')}
                {situacoes.map(([nome, qtd]) => (
                  <span key={nome}>{chip(situacao === nome, () => setSituacao(nome), `${nome} (${qtd})`)}</span>
                ))}
                {temFiltro && (
                  <button
                    type="button"
                    onClick={() => { setSoComEstoque(false); setSituacao(''); }}
                    style={{
                      background: 'transparent', border: 'none', color: 'var(--portal-muted)',
                      fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline',
                    }}
                  >
                    limpar
                  </button>
                )}
              </div>
            )}

            {/* Com filtro ligado, dizer quantos ficaram de fora evita a conclusão
                errada de que a busca não achou. */}
            {temFiltro && resultados !== null && resultados.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: 'var(--portal-muted)', marginBottom: 8 }}>
                mostrando {lista.length} de {resultados.length}
              </div>
            )}

            {resultados === null && (
              <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                Busque pelo nome, pelo código do produto ou pela referência do fabricante.
                Preço, estoque e campanha vêm do ERP na hora.
              </p>
            )}
            {resultados !== null && resultados.length === 0 && !buscando && (
              <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                Nenhum produto encontrado. Tente outra palavra do nome, o código ou a referência.
              </p>
            )}
            {resultados !== null && resultados.length > 0 && lista.length === 0 && (
              <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem' }}>
                Os {resultados.length} resultados ficaram de fora pelo filtro.{' '}
                <button
                  type="button"
                  onClick={() => { setSoComEstoque(false); setSituacao(''); }}
                  style={{
                    background: 'transparent', border: 'none', color: VERDE,
                    padding: 0, cursor: 'pointer', textDecoration: 'underline', fontSize: '0.85rem',
                  }}
                >
                  limpar o filtro
                </button>
              </p>
            )}

            {verEstoque && (
              <ModalEstoque
                produto={verEstoque}
                filialDoUsuario={verEstoque.estoque_aqui?.filial}
                onFechar={() => setVerEstoque(null)}
              />
            )}

            {mandando && (
              <EscolherEnvio
                produto={mandando}
                conversaId={conversaId}
                onFechar={() => setMandando(null)}
              />
            )}

            {lista.map((p) => (
              <LinhaProduto
                key={p.codpro}
                produto={p}
                conversaId={conversaId}
                onAmpliar={setAmpliada}
                onMandar={setMandando}
                onVerEstoque={setVerEstoque}
                naLista={estaNaLista(p)}
                onAlternarLista={alternarLista}
                largo={largo}
              />
            ))}
          </div>

          {/* A lista mora no rodapé do modal, que fica parado enquanto a busca
              rola: dá pra ver quantos já estão escolhidos a qualquer momento,
              em qualquer busca. Fechada mostra só o número; aberta, o que é. */}
          {naLista.length > 0 && (
            <div className="modal-footer" style={{
              borderColor: 'var(--portal-border)', display: 'block', padding: '8px 14px',
            }}>
              {listaAberta && (
                <div style={{ maxHeight: '55vh', overflowY: 'auto', marginBottom: 8 }}>
                  {naLista.map((p, i) => (
                    <div key={p.codpro} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0',
                      borderBottom: '1px solid var(--portal-border)', fontSize: '0.86rem',
                    }}>
                      <span style={{ color: 'var(--portal-muted)', width: 18, textAlign: 'right', flexShrink: 0 }}>{i + 1}.</span>
                      <MiniaturaLista produto={p} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>
                          {p.descricao}
                        </span>
                        <span style={{ color: 'var(--portal-muted)', fontSize: '0.74rem' }}>{p.codpro}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => alternarLista(p)}
                        disabled={Boolean(enviandoLista)}
                        title="Tirar da lista"
                        aria-label="Tirar da lista"
                        style={{
                          width: 40, height: 40, flexShrink: 0,
                          background: 'transparent', border: 'none', color: 'var(--portal-muted)',
                          cursor: enviandoLista ? 'default' : 'pointer', fontSize: '1rem',
                        }}
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    </div>
                  ))}
                  {!enviandoLista && (
                    <button
                      type="button"
                      onClick={() => { setNaLista([]); setListaAberta(false); }}
                      style={{
                        background: 'transparent', border: 'none', color: 'var(--portal-muted)',
                        fontSize: '0.75rem', cursor: 'pointer', textDecoration: 'underline', padding: '6px 0 0',
                      }}
                    >
                      limpar lista
                    </button>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setListaAberta((v) => !v)}
                  style={{
                    background: 'transparent', border: '1px solid var(--portal-border)',
                    color: 'var(--portal-text)', borderRadius: 6, padding: '5px 11px',
                    fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}
                >
                  <i className="bi bi-list-check me-1" />
                  {naLista.length} na lista
                  <i className={`bi ${listaAberta ? 'bi-chevron-down' : 'bi-chevron-up'} ms-1`} />
                </button>
                <span style={{ flex: 1 }} />
                {enviandoLista ? (
                  <span style={{ fontSize: '0.82rem', color: VERDE, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span className="screen-loader__spinner" style={{ width: 15, height: 15, borderWidth: 2, display: 'inline-block' }} />
                    Enviando {Math.min(enviandoLista.feitos + 1, enviandoLista.total)} de {enviandoLista.total}…
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setListaAberta(false); setEscolhendoModoLista(true); }}
                    style={{
                      background: VERDE, color: '#fff', border: 'none', borderRadius: 6,
                      padding: '6px 14px', fontSize: '0.82rem', fontWeight: 600,
                      cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    <i className="bi bi-send me-1" />
                    {naLista.length === 1 ? 'Mandar o produto' : `Mandar os ${naLista.length}`}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* A escolha de como mandar a lista mora FORA do corpo do modal. Dentro
          dele, com a lista aberta, ela herdava a parte apagada (opacidade e o
          toque que só fecha a lista) e ficava esmaecida, por baixo da lista e
          sem responder — o vendedor tocava em "com preço à vista" e nada
          saía. */}
      {escolhendoModoLista && (
        <EscolherEnvioLista
          quantidade={naLista.length}
          onEscolher={enviarLista}
          onFechar={() => setEscolhendoModoLista(false)}
        />
      )}
    </div>
  );
}
